/**
 * @fileoverview Authenticate and CRUD with backend API.
 *
 * Phase 0.5 (2026-04-10): Session-cookie auth replaces the old JWT flow.
 *
 * Bootstrap sequence on construction:
 *   1. GET /api/session_auth/csrf/  — primes the csrftoken cookie.
 *   2. GET /api/session_auth/whoami/ — reports whether the existing
 *      sessionid cookie (if any) still identifies a logged-in user.
 *   3. If authenticated → load assets, open socket.
 *      If not → fire lostAuthenticationCallback so the UI shows login.
 *
 * Login/logout:
 *   - POST /api/session_auth/login/  {username, password}
 *   - POST /api/session_auth/logout/
 *
 * Every request uses credentials: 'include' so the browser sends
 * sessionid + csrftoken cookies. State-changing requests (POST/PUT/
 * PATCH/DELETE) additionally send an X-CSRFToken header read from
 * the csrftoken cookie — Django's CsrfViewMiddleware requires the
 * header to match the cookie value.
 *
 * No tokens are stored in localStorage. The session cookie is
 * HttpOnly and managed entirely by the browser.
 */

import moment from "moment-timezone";
import {API_URL} from "../../Settings";
import {isLikelyEmbedded} from "../../discord/embeddedDetect";
import {SocketHandler} from "./SocketHandler";


const CSRF_COOKIE_NAME = 'csrftoken';
const STATE_CHANGING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Window-scoped flag set by DiscordProvider during the Activity
// handshake. Read here so that, in embedded mode, the auth bootstrap
// can wait for the handshake to set its session cookie before
// dropping to the login screen.
const DISCORD_AUTH_STATE_FLAG = '__discordAuthState__';
const DISCORD_AUTH_COMPLETE_EVENT = 'discord-auth-complete';
const DISCORD_AUTH_FAILED_EVENT = 'discord-auth-failed';


/**
 * Read a cookie value by name. Returns null if the cookie isn't set.
 * The csrftoken cookie is intentionally NOT HttpOnly so the SPA can
 * read it and echo it back in the X-CSRFToken header.
 */
function readCookie(name) {
    const prefix = name + '=';
    const parts = document.cookie ? document.cookie.split(';') : [];
    for (let part of parts) {
        part = part.trim();
        if (part.indexOf(prefix) === 0) {
            return decodeURIComponent(part.substring(prefix.length));
        }
    }
    return null;
}


/**
 * The main class for interfacing with the Django backend via session auth.
 */
export default class API {

    TIMEOUT = 6000;

    // Embedded-mode bootstrap waits up to this long for the Discord
    // handshake to land its session cookie before dropping to the
    // login screen. Static so tests can shorten it.
    static DISCORD_AUTH_TIMEOUT_MS = 5000;

    /**
     * @param {object} parent enables references to other objects.
     * @param {function} lostAuthenticationCallback when auth is lost.
     * @param {function} isAuthenticatedCallback when auth is established.
     */
    constructor(parent,
                lostAuthenticationCallback,
                isAuthenticatedCallback) {
        this.parent = parent;
        this.lostAuthenticationCallback = lostAuthenticationCallback;
        this.isAuthenticatedCallback = isAuthenticatedCallback;

        this.authenticating = true;
        this.isAuthenticated = false;
        this.user = null;
        this.account = null;

        this.fetchQueue = {};

        this.timeZoneName = moment.tz.guess(true);
        this.timeZone = moment.tz.zone(this.timeZoneName);
        this.timeZoneAbbreviation = this.timeZone.abbr(moment());

        this.isError = null;
        this.socketHandler = null;

        this.errorCallback = this.errorCallback.bind(this);
        this.responseProcessor = this.responseProcessor.bind(this);

        this.clearAuthentication = this.clearAuthentication.bind(this);
        this.setIsAuthenticated = this.setIsAuthenticated.bind(this);
        this.validateCredentials = this.validateCredentials.bind(this);

        this.tryAgain = this.tryAgain.bind(this);
        this.processQueue = this.processQueue.bind(this);

        this.login = this.login.bind(this);
        this.loginWithCallback = this.loginWithCallback.bind(this);
        this.logout = this.logout.bind(this);
        this.registerAnonymous = this.registerAnonymous.bind(this);
        this.upgradeAccount = this.upgradeAccount.bind(this);
        this.waitForDiscordAuthThenFinish =
            this.waitForDiscordAuthThenFinish.bind(this);
        this.waitForDiscordAuthThenReconcile =
            this.waitForDiscordAuthThenReconcile.bind(this);
        this.revalidateAfterDiscord =
            this.revalidateAfterDiscord.bind(this);
        this.reconcileIdentityAfterDiscord =
            this.reconcileIdentityAfterDiscord.bind(this);

        this.getAccountCallback = this.getAccountCallback.bind(this);
        this.getAccountCallbackFailure =
            this.getAccountCallbackFailure.bind(this);

        this.loadAssetsAndSetIsAuthenticated =
            this.loadAssetsAndSetIsAuthenticated.bind(this);

        this.validateCredentials();
    }

    /**
     * Auth boot: validateCredentials primes the CSRF cookie, then asks
     * the backend whether the existing session is still valid. If it
     * is, load assets. If not (and not waiting on Discord), drops to
     * the unauthenticated UI tree (login screen) instead of
     * auto-creating an anonymous account.
     *
     * 2026-05-26: anon auto-provisioning removed from the boot path.
     * The anon endpoint and `registerAnonymous` method are still
     * available for the explicit Upgrade flow, but the unauthenticated
     * boot path no longer fires them. Email verification is back —
     * see RegistrationResponse + /api/user_registration_confirm/.
     */
    validateCredentials() {
        this.authenticating = true;
        const csrfUrl = this.getUrl('session_auth/csrf');
        fetch(csrfUrl, {method: 'get', credentials: 'include'})
            .then(() => {
                const whoamiUrl = this.getUrl('session_auth/whoami');
                return fetch(whoamiUrl,
                    {method: 'get', credentials: 'include'});
            })
            .then((response) => this.responseToJson(response))
            .then((data) => {
                if (data && data.is_authenticated) {
                    this.user = data;
                    // Warm-cache embedded race: this cached session may
                    // belong to a DIFFERENT user than the one the Discord
                    // handshake is about to log in (e.g. a prior player's
                    // session cookie still in the discordsays.com origin
                    // store). Opening the socket now binds the
                    // UserConnection to the stale identity for the life of
                    // the page. Wait for the handshake to settle and
                    // reconcile the identity BEFORE opening the socket.
                    if (isLikelyEmbedded()) {
                        return this.waitForDiscordAuthThenReconcile(data);
                    }
                    this.loadAssetsAndSetIsAuthenticated();
                    return null;
                }
                // In embedded mode, the Discord handshake runs in
                // parallel and will set a session cookie. Wait for it
                // before dropping to the login screen — otherwise we
                // race ahead and the player sees a login flash before
                // the handshake completes.
                if (isLikelyEmbedded()) {
                    return this.waitForDiscordAuthThenFinish();
                }
                return this.clearAuthentication();
            })
            .catch((error) => {
                console.warn('validateCredentials failed', error);
                this.clearAuthentication();
            });
    }

    /**
     * Embedded-mode bootstrap. Yield to DiscordProvider's handshake:
     * if it completes, re-fire whoami (the cookie is now set) and
     * finish; if it fails or times out, fall through to the login
     * screen so the player can still sign in manually.
     *
     * Checks the window-scoped DiscordProvider flag on entry so a
     * handshake that already finished (faster than our whoami chain)
     * doesn't strand us listening for an event nobody else will fire.
     */
    waitForDiscordAuthThenFinish() {
        const state = (typeof window !== 'undefined')
            ? window[DISCORD_AUTH_STATE_FLAG]
            : undefined;
        if (state === 'complete') {
            console.info('[API] discord-auth already complete; revalidating');
            return this.revalidateAfterDiscord();
        }
        if (state === 'failed') {
            console.info('[API] discord-auth already failed; dropping'
                + ' to login');
            this.clearAuthentication();
            return Promise.resolve(null);
        }
        return new Promise((resolve) => {
            let settled = false;

            const onComplete = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                console.info('[API] discord-auth-complete received;'
                    + ' revalidating');
                this.revalidateAfterDiscord().then(resolve);
            };

            const onFail = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                console.info('[API] discord-auth-failed received;'
                    + ' dropping to login');
                this.clearAuthentication();
                resolve(null);
            };

            const onTimeout = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                console.info('[API] discord-auth timed out after %sms;'
                    + ' dropping to login',
                    API.DISCORD_AUTH_TIMEOUT_MS);
                this.clearAuthentication();
                resolve(null);
            };

            const timer = setTimeout(onTimeout, API.DISCORD_AUTH_TIMEOUT_MS);
            const cleanup = () => {
                clearTimeout(timer);
                window.removeEventListener(
                    DISCORD_AUTH_COMPLETE_EVENT, onComplete);
                window.removeEventListener(
                    DISCORD_AUTH_FAILED_EVENT, onFail);
            };

            window.addEventListener(DISCORD_AUTH_COMPLETE_EVENT, onComplete);
            window.addEventListener(DISCORD_AUTH_FAILED_EVENT, onFail);
        });
    }

    /**
     * Warm-cache embedded bootstrap. The first whoami already returned
     * authenticated for some pre-Discord identity (`preDiscordUser`),
     * but in embedded mode the Discord handshake runs in parallel and
     * may rebind the session cookie to a DIFFERENT user. Yield to the
     * handshake before opening the socket, then reconcile:
     *
     *   complete  → re-run whoami, adopt whatever identity the cookie
     *               now resolves to (logging any swap), then load.
     *   failed    → keep the pre-Discord identity and load — the HSH
     *               session is still valid, only the Discord leg blew up.
     *   timeout   → same as failed.
     *
     * The socket has NOT been opened yet on this path, so an identity
     * swap needs no tear-down — openSocket() lazily constructs the
     * SocketHandler with the now-correct cookie at the end of the load.
     *
     * Checks the window-scoped DiscordProvider flag on entry so a
     * handshake that already finished doesn't strand us listening for
     * an event nobody else will fire.
     */
    waitForDiscordAuthThenReconcile(preDiscordUser) {
        const state = (typeof window !== 'undefined')
            ? window[DISCORD_AUTH_STATE_FLAG]
            : undefined;
        if (state === 'complete') {
            console.info('[API] discord-auth already complete;'
                + ' reconciling identity');
            return this.reconcileIdentityAfterDiscord(preDiscordUser);
        }
        if (state === 'failed') {
            console.info('[API] discord-auth already failed; keeping'
                + ' pre-discord identity');
            this.loadAssetsAndSetIsAuthenticated();
            return Promise.resolve(null);
        }
        return new Promise((resolve) => {
            let settled = false;

            const onComplete = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                console.info('[API] discord-auth-complete received;'
                    + ' reconciling identity');
                this.reconcileIdentityAfterDiscord(preDiscordUser)
                    .then(resolve);
            };

            const onFail = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                console.info('[API] discord-auth-failed received; keeping'
                    + ' pre-discord identity');
                this.loadAssetsAndSetIsAuthenticated();
                resolve(null);
            };

            const onTimeout = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                console.info('[API] discord-auth timed out after %sms;'
                    + ' keeping pre-discord identity',
                    API.DISCORD_AUTH_TIMEOUT_MS);
                this.loadAssetsAndSetIsAuthenticated();
                resolve(null);
            };

            const timer = setTimeout(onTimeout, API.DISCORD_AUTH_TIMEOUT_MS);
            const cleanup = () => {
                clearTimeout(timer);
                window.removeEventListener(
                    DISCORD_AUTH_COMPLETE_EVENT, onComplete);
                window.removeEventListener(
                    DISCORD_AUTH_FAILED_EVENT, onFail);
            };

            window.addEventListener(DISCORD_AUTH_COMPLETE_EVENT, onComplete);
            window.addEventListener(DISCORD_AUTH_FAILED_EVENT, onFail);
        });
    }

    /**
     * Run a second whoami after the Discord handshake has completed.
     * The cookie should now be set, so this typically returns
     * authenticated. If the cookie didn't take for some reason, fall
     * through to the login screen.
     */
    revalidateAfterDiscord() {
        const whoamiUrl = this.getUrl('session_auth/whoami');
        return fetch(whoamiUrl, {method: 'get', credentials: 'include'})
            .then((response) => this.responseToJson(response))
            .then((data) => {
                if (data && data.is_authenticated) {
                    this.user = data;
                    this.loadAssetsAndSetIsAuthenticated();
                    return null;
                }
                console.warn('[API] post-discord whoami returned'
                    + ' not-authenticated; dropping to login');
                return this.clearAuthentication();
            });
    }

    /**
     * Re-run whoami after the Discord handshake completed on the
     * warm-cache path, and reconcile against the pre-Discord identity:
     *
     *   not-authenticated → the cookie got cleared during the
     *       handshake; drop to login.
     *   same user.id      → no rebind (or rebound to itself); adopt and
     *       load.
     *   different user.id → the handshake logged in a DIFFERENT user
     *       (the live 2026-05-30 GeneralDread-over-Scipraxian race).
     *       Log the swap, adopt the post-Discord identity, and load —
     *       the socket opens against the correct cookie.
     */
    reconcileIdentityAfterDiscord(preDiscordUser) {
        const whoamiUrl = this.getUrl('session_auth/whoami');
        return fetch(whoamiUrl, {method: 'get', credentials: 'include'})
            .then((response) => this.responseToJson(response))
            .then((data) => {
                if (!data || !data.is_authenticated) {
                    console.warn('[API] post-discord whoami returned'
                        + ' not-authenticated; dropping to login');
                    return this.clearAuthentication();
                }
                const preId = preDiscordUser && preDiscordUser.id;
                if (data.id !== preId) {
                    console.info('[API] identity swap after discord'
                        + ' handshake: pre=%s post=%s; adopting'
                        + ' post-discord user', preId, data.id);
                }
                this.user = data;
                this.loadAssetsAndSetIsAuthenticated();
                return null;
            });
    }

    /**
     * Provision an anonymous guest account and log the client in.
     * As of 2026-05-26 this is no longer fired from the auth boot
     * path — it remains available for explicit callers (e.g. the
     * upgrade-from-anon flow's preconditions, or any future
     * one-click-play surface) and is still wired in the backend.
     * Safe to re-invoke: the backend treats an already-authed session
     * as a no-op and just returns the current identity.
     */
    registerAnonymous() {
        const url = this.getUrl('session_auth/anon');
        const body = JSON.stringify({
            timezone: this.timeZoneName,
        });
        const request = this.buildRequest('post', null, body);
        return fetch(url, request)
            .then((response) => {
                if (!response.ok) {
                    return this.responseToJson(response).then((data) => {
                        console.warn('anon register failed', data);
                        this.clearAuthentication();
                        return null;
                    });
                }
                return this.responseToJson(response).then((data) => {
                    this.user = data;
                    this.loadAssetsAndSetIsAuthenticated();
                    return data;
                });
            })
            .catch((error) => {
                console.warn('anon register fetch failed', error);
                this.clearAuthentication();
            });
    }

    /**
     * Upgrade the current (anonymous) session to a named account.
     * Body shape: {username?, password, email?, first_name?, last_name?}.
     * The session cookie is preserved across the password change so the
     * player stays logged in.
     */
    upgradeAccount(data, successCallback, failureCallback) {
        const body = JSON.stringify(data);
        const url = this.getUrl('session_auth/upgrade');
        const request = this.buildRequest('post', null, body);
        fetch(url, request)
            .then((response) => {
                if (response.ok) {
                    return this.responseToJson(response).then((payload) => {
                        this.user = payload;
                        if (successCallback) {
                            successCallback(payload);
                        }
                        return payload;
                    });
                }
                return this.responseToJson(response).then((payload) => {
                    if (failureCallback) {
                        failureCallback(payload);
                    }
                    return null;
                });
            })
            .catch((error) => {
                console.warn('upgradeAccount fetch failed', error);
                if (failureCallback) {
                    failureCallback({detail: 'Network error'});
                }
            });
    }

    getAccountCallback(data) {
        if (!data || data === {}) {
            console.warn('Account will need to retry.');
            return;
        }
        this.account = data[0];
        this.openSocket(); // upon open this calls this.setIsAuthenticated();
    }

    getAccountCallbackFailure(data) {
        console.warn('getAccountCallbackFailure', data);
        this.logout();
    }

    getAccount() {
        this.queuedGet('account',
            this.getAccountCallback, this.getAccountCallbackFailure);
    }

    loadAssetsAndSetIsAuthenticated() {
        this.getAccount();
    }

    logout() {
        const url = this.getUrl('session_auth/logout');
        const request = this.buildRequest('post', null, null);
        return fetch(url, request)
            .catch((error) => {
                console.warn('logout request failed', error);
            })
            .finally(() => {
                this.clearAuthentication();
                this.closeSocket();
            });
    }

    start() {
        // No-op under session auth. Kept for compatibility with callers
        // that invoked it in the JWT era to arm the refresh timer.
    }

    login(username, password) {
        this.loginWithCallback(
            username, password, () => {}, (data) => {
                console.warn('login failure', data);
            });
    }

    loginWithCallback(username, password, successCallback, failureCallback) {
        const body = JSON.stringify({username, password});
        const url = this.getUrl('session_auth/login');
        const request = this.buildRequest('post', null, body);
        fetch(url, request)
            .then((response) => {
                if (response.ok) {
                    return this.responseToJson(response).then((data) => {
                        this.user = data;
                        successCallback(data);
                        this.loadAssetsAndSetIsAuthenticated();
                        return data;
                    });
                }
                return this.responseToJson(response).then((data) => {
                    failureCallback(data);
                    this.clearAuthentication();
                    return null;
                });
            })
            .catch((error) => {
                console.warn('login fetch failed', error);
                failureCallback({detail: 'Network error'});
                this.clearAuthentication();
            });
    }

    clearAuthentication() {
        this.isAuthenticated = false;
        this.user = null;
        this.account = null;
        if (this.lostAuthenticationCallback) {
            this.lostAuthenticationCallback();
        }
        this.authenticating = false;
    }

    /**
     * Engages application's callback. Called by open socket.
     */
    setIsAuthenticated = () => {
        this.isAuthenticated = Date.now();
        this.authenticating = false;
        this.isAuthenticatedCallback();
        this.processQueue();
    }

    processQueue() {
        for (const key in this.fetchQueue) {
            if (this.fetchQueue[key].status === 'waiting-for-retry') {
                this.tryAgain(key);
            }
        }
    }

    tryAgain(key) {
        this.fetchQueue[key].status = 'in-progress';
        const queuedItem = this.fetchQueue[key];
        const url = queuedItem.url;
        const method = queuedItem.method;
        const headers = queuedItem.headers;
        const callBack = queuedItem.callBack;
        const errorCallback = queuedItem.errorCallback;
        const body = queuedItem.body;
        this.queuedFetch(
            url, method, headers, callBack, errorCallback, body, key);
    }

    errorCallback(event) {
        if (event && event.error) {
            if (event.error === '401 - Unauthorized') {
                // Swallow — responseProcessor handles this.
            }
        }
    }

    getUrl(accessPoint, recordId = null, args = null) {
        let url = API_URL;
        if (accessPoint) {
            url += accessPoint + '/';
        }
        if (recordId) {
            url += (recordId).toString() + '/';
        }
        if (args) {
            url += '?';
            for (let [key, value] of Object.entries(args)) {
                url += `${key}=${value}&`;
            }
            if (url.endsWith('&')) {
                url = url.slice(0, -1);
            }
        }
        return url;
    }

    responseToJson(response) {
        return response.text().then(function (text) {
            if (text) {
                try {
                    return JSON.parse(text);
                } catch (error) {
                    console.warn('not JSON', error);
                    return {};
                }
            } else {
                return {};
            }
        });
    }

    errorHandler(response, errorCallback) {
        const jsonResponse = this.responseToJson(response);
        if (errorCallback) {
            jsonResponse.then((data) => {
                errorCallback(data, response.status);
            });
        }
        this.isError = true;
        return null;
    }

    responseProcessor(response, key = null, clearAuthentication = false,
                      errorCallback = null) {
        if (response.ok) {
            if (key) {
                delete this.fetchQueue[key];
            }
            return this.responseToJson(response);
        }
        if (response.status === 401 || response.status === 403) {
            // Session expired / not authenticated. Under session auth
            // there is nothing to "refresh" — drop to login.
            this.clearAuthentication();
            return this.errorHandler(response, errorCallback);
        }
        return this.errorHandler(response, errorCallback);
    }

    buildRequest(method, headers, body) {
        const lowerMethod = (method || 'get').toLowerCase();
        const finalHeaders = Object.assign({}, headers || {});
        if (STATE_CHANGING_METHODS.has(lowerMethod)) {
            const csrfToken = readCookie(CSRF_COOKIE_NAME);
            if (csrfToken) {
                finalHeaders['X-CSRFToken'] = csrfToken;
            }
        }
        const request = {
            method: method,
            headers: finalHeaders,
            credentials: 'include',
        };
        if (body !== null && body !== undefined) {
            request.body = body;
            request.headers = Object.assign({}, finalHeaders, {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            });
        }
        return request;
    }

    queuedFetch = (
        url, method, headers,
        callBack, errorCallback,
        body = null, key = null) => {
        const request = this.buildRequest(method, headers, body);
        if (!key) {
            key = Date.now();
            this.fetchQueue[key] = {
                url: url,
                request: request,
                method: method,
                headers: headers,
                callBack: callBack,
                errorCallback: errorCallback,
                body: body,
                status: 'fetching',
                retryCount: 0,
            };
        } else {
            this.fetchQueue[key].retryCount += 1;
            if (this.fetchQueue[key].retryCount > 5) {
                console.warn('API.queuedFetch retry limit reached');
                this.clearAuthentication();
                return;
            }
        }

        this.isError = false;

        fetch(url, request)
            .then((response) =>
                this.responseProcessor(response, key, false, errorCallback))
            .then((data) => {
                if (!this.isError) {
                    if ((!!data && (!!data.error || !!data.detail))
                            || !data) {
                        errorCallback(data);
                    } else {
                        if (callBack) {
                            callBack(data);
                        }
                    }
                }
            })
            .catch(errorCallback);
    };

    queuedGet = (accessPoint, callBack, errorCallback = null,
                 recordId = null, args = null) => {
        const url = this.getUrl(accessPoint, recordId, args);
        if (!errorCallback) {
            errorCallback = this.errorCallback;
        }
        this.queuedFetch(url, 'get', null, callBack, errorCallback);
    };

    queuedPut = (accessPoint, recordId, body, callBack,
                 errorCallback = null) => {
        body = JSON.stringify(body);
        const url = this.getUrl(accessPoint, recordId);
        if (!errorCallback) {
            errorCallback = this.errorCallback;
        }
        this.queuedFetch(url, 'put', null, callBack, errorCallback, body);
    };

    queuedPatch = (accessPoint, recordId, body, callBack,
                   errorCallback = null) => {
        body = JSON.stringify(body);
        const url = this.getUrl(accessPoint, recordId);
        if (!errorCallback) {
            errorCallback = this.errorCallback;
        }
        this.queuedFetch(url, 'patch', null, callBack, errorCallback, body);
    };

    queuedPost = (accessPoint, body, callBack, errorCallback = null) => {
        body = JSON.stringify(body);
        const url = this.getUrl(accessPoint);
        if (!errorCallback) {
            errorCallback = this.errorCallback;
        }
        this.queuedFetch(url, 'post', null, callBack, errorCallback, body);
    };

    queuedDelete = (accessPoint, recordId, callBack, errorCallback = null,
                    args = null) => {
        const url = this.getUrl(accessPoint, recordId, args);
        if (!errorCallback) {
            errorCallback = this.errorCallback;
        }
        this.queuedFetch(url, 'delete', null, callBack, errorCallback);
    };

    fetch = (url, method, headers, callBack, errorCallback, body = null) => {
        const request = this.buildRequest(method, headers, body);
        fetch(url, request)
            .then((response) => this.responseProcessor(response, null))
            .then((data) => {
                if (!!data && !!data.error) {
                    if (errorCallback) {
                        errorCallback(data);
                    }
                } else {
                    callBack(data);
                }
            })
            .catch(errorCallback);
    };

    get = (accessPoint, callBack, errorCallback = null,
           recordId = null, args = null) => {
        const url = this.getUrl(accessPoint, recordId, args);
        if (!errorCallback) {
            errorCallback = this.errorCallback;
        }
        this.fetch(url, 'get', null, callBack, errorCallback);
    };

    put = (accessPoint, recordId, body, callBack, errorCallback = null) => {
        body = JSON.stringify(body);
        const url = this.getUrl(accessPoint, recordId);
        if (!errorCallback) {
            errorCallback = this.errorCallback;
        }
        this.fetch(url, 'put', null, callBack, errorCallback, body);
    };

    post = (accessPoint, body, callBack, errorCallback = null) => {
        body = JSON.stringify(body);
        const url = this.getUrl(accessPoint);
        if (!errorCallback) {
            errorCallback = this.errorCallback;
        }
        this.fetch(url, 'post', null, callBack, errorCallback, body);
    };

    delete = (accessPoint, recordId, callBack, errorCallback = null,
              args = null) => {
        const url = this.getUrl(accessPoint, recordId, args);
        if (!errorCallback) {
            errorCallback = this.errorCallback;
        }
        this.fetch(url, 'delete', null, callBack, errorCallback);
    };

    postFile = (accessPoint, file, data, callBack, errorCallback) => {
        const url = this.getUrl(accessPoint);
        const headers = {Accept: 'application/json'};
        const csrfToken = readCookie(CSRF_COOKIE_NAME);
        if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
        }

        const formdata = new FormData();
        formdata.append('file', file);
        for (const [key, value] of Object.entries(data)) {
            formdata.append(key, value);
        }

        const request = {
            method: 'post',
            headers: headers,
            body: formdata,
            credentials: 'include',
        };

        fetch(url, request)
            .then((response) => this.responseProcessor(response, null))
            .then((responseData) => {
                if (!!responseData && !!responseData.error) {
                    if (errorCallback) {
                        errorCallback(responseData);
                    }
                } else {
                    callBack(responseData);
                }
            })
            .catch(errorCallback);
    }

    openSocket = () => {
        if (!this.socketHandler) {
            this.socketHandler = new SocketHandler(this);
        } else {
            this.socketHandler.openSocket();
            this.setIsAuthenticated();
        }
    }

    sendMessage = (typeName, packageValue) => {
        console.log('API.sendMessage', typeName, packageValue);
        this.socketHandler.send(typeName, packageValue);
    }

    sendPackagedMessage = (message) => {
        console.log('API.sendPackagedMessage', message);
        this.socketHandler.sendPackagedMessage(message);
    }

    closeSocket = () => {
        if (this.socketHandler) {
            this.socketHandler.closeSocket();
        }
    }
}
